import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Typography, Button, TextField, IconButton, Chip, Select, MenuItem,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import CheckIcon from '@material-ui/icons/Check';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { AuthContext } from '../../App';
import ConfirmDialog from '../common/ConfirmDialog';

const FLEET_COLORS = [
  { value: '#3b82f6', label: 'Azul' },
  { value: '#22c55e', label: 'Verde' },
  { value: '#f59e0b', label: 'Âmbar' },
  { value: '#ef4444', label: 'Vermelho' },
  { value: '#8b5cf6', label: 'Roxo' },
  { value: '#06b6d4', label: 'Cyan' },
];

const useStyles = makeStyles(() => ({
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)',
    backdropFilter: 'blur(8px)', zIndex: 1300,
    display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
  },
  dialog: {
    background: '#fff', borderRadius: 16, width: 520, maxWidth: '95vw',
    maxHeight: '85vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px', background: 'linear-gradient(180deg, #fff 0%, #eff6ff 100%)',
    borderBottom: '1px solid rgba(0,0,0,0.06)',
  },
  content: { flex: 1, overflowY: 'auto', padding: '12px 20px' },
  fleetCard: {
    border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 8,
    overflow: 'hidden', transition: 'all 0.15s',
    '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  },
}));

function ManageFleetsDialog({ open, onClose, lang = 'pt' }) {
  const classes = useStyles();
  const { token } = useContext(AuthContext);
  const pt = lang === 'pt';

  const [fleets, setFleets] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#3b82f6');
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  async function fetchData() {
    if (!token) return;
    setLoading(true);
    try {
      const [fRes, vRes] = await Promise.all([
        fetch('/planner-api/fleets', { headers }),
        fetch('/planner-api/vehicles', { headers }),
      ]);
      setFleets(await fRes.json());
      setVehicles(await vRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }

  useEffect(() => { if (open) fetchData(); }, [open, token]);

  async function handleCreate() {
    if (!newName.trim()) return;
    await fetch('/planner-api/fleets', { method: 'POST', headers, body: JSON.stringify({ name: newName.trim(), color: newColor }) });
    setNewName(''); fetchData();
  }

  async function handleDelete(id) {
    setDeleteConfirm(id);
  }
  async function confirmDelete() {
    if (!deleteConfirm) return;
    await fetch(`/planner-api/fleets/${deleteConfirm}`, { method: 'DELETE', headers });
    setDeleteConfirm(null);
    fetchData();
  }

  async function handleSaveEdit(id) {
    if (!editName.trim()) return;
    await fetch(`/planner-api/fleets/${id}`, { method: 'PATCH', headers, body: JSON.stringify({ name: editName.trim() }) });
    setEditingId(null); fetchData();
  }

  async function handleAssignVehicle(fleetId, vehicleId) {
    await fetch(`/planner-api/fleets/${fleetId}/vehicles`, { method: 'POST', headers, body: JSON.stringify({ vehicleId }) });
    fetchData();
  }

  async function handleUnassignVehicle(fleetId, vehicleId) {
    await fetch(`/planner-api/fleets/${fleetId}/vehicles/${vehicleId}`, { method: 'DELETE', headers });
    fetchData();
  }

  if (!open) return null;

  const assignedVehicleIds = fleets.flatMap(f => (f.vehicles || []).map(v => v.id));
  const unassignedVehicles = vehicles.filter(v => !assignedVehicleIds.includes(v.id));

  return (<>
    <Box className={classes.overlay} onClick={onClose}>
      <Box className={classes.dialog} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <Box className={classes.header}>
          <Box style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LocalShippingIcon style={{ fontSize: 20, color: '#3b82f6' }} />
            <Typography style={{ fontSize: '0.92rem', fontWeight: 700, color: '#1e293b' }}>
              {pt ? 'Gerir Frotas' : 'Manage Fleets'}
            </Typography>
            <Chip label={fleets.length} size="small"
              style={{ height: 20, fontSize: '0.6rem', fontWeight: 700, background: '#dbeafe', color: '#2563eb' }} />
          </Box>
          <Button size="small" onClick={onClose} startIcon={<CloseIcon style={{ fontSize: 14 }} />}
            style={{ fontSize: '0.72rem', color: '#64748b', textTransform: 'none', fontWeight: 600, borderRadius: 8, border: '1px solid #e2e8f0', padding: '4px 12px' }}>
            {pt ? 'Fechar' : 'Close'}
          </Button>
        </Box>

        <Box className={classes.content}>
          {/* Create new fleet */}
          <Box style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
            <TextField size="small" variant="outlined" placeholder={pt ? 'Nome da frota...' : 'Fleet name...'}
              value={newName} onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreate()}
              style={{ flex: 1 }} inputProps={{ style: { fontSize: '0.78rem', padding: '8px 10px' } }} />
            <Box style={{ display: 'flex', gap: 3 }}>
              {FLEET_COLORS.map(c => (
                <Box key={c.value} onClick={() => setNewColor(c.value)} style={{
                  width: 20, height: 20, borderRadius: 6, background: c.value, cursor: 'pointer',
                  border: newColor === c.value ? '2px solid #1e293b' : '2px solid transparent',
                  transition: 'all 0.15s',
                }} />
              ))}
            </Box>
            <Button size="small" variant="contained" onClick={handleCreate} disabled={!newName.trim()}
              startIcon={<AddIcon style={{ fontSize: 14 }} />}
              style={{ fontSize: '0.72rem', textTransform: 'none', borderRadius: 8, background: '#3b82f6', color: '#fff', whiteSpace: 'nowrap' }}>
              {pt ? 'Criar' : 'Create'}
            </Button>
          </Box>

          {/* Fleet list */}
          {fleets.map(fleet => {
            const isExpanded = expandedId === fleet.id;
            const isEditing = editingId === fleet.id;
            const fleetVehicles = fleet.vehicles || [];

            return (
              <Box key={fleet.id} className={classes.fleetCard}>
                {/* Fleet header */}
                <Box style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px',
                  cursor: 'pointer', background: isExpanded ? '#f8fafc' : '#fff',
                }} onClick={() => setExpandedId(isExpanded ? null : fleet.id)}>
                  <Box style={{ width: 12, height: 12, borderRadius: 4, background: fleet.color || '#3b82f6', flexShrink: 0 }} />
                  {isEditing ? (
                    <TextField size="small" variant="standard" value={editName}
                      onChange={e => setEditName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSaveEdit(fleet.id)}
                      onClick={e => e.stopPropagation()}
                      autoFocus style={{ flex: 1 }}
                      inputProps={{ style: { fontSize: '0.78rem', fontWeight: 600 } }} />
                  ) : (
                    <Typography style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', flex: 1 }}>
                      {fleet.name}
                    </Typography>
                  )}
                  <Chip label={`${fleet.vehicleCount || fleetVehicles.length} ${pt ? 'veículos' : 'vehicles'}`}
                    size="small" style={{ height: 18, fontSize: '0.58rem', fontWeight: 600, background: '#f1f5f9', color: '#64748b' }} />
                  {isEditing ? (
                    <IconButton size="small" onClick={e => { e.stopPropagation(); handleSaveEdit(fleet.id); }} style={{ color: '#22c55e' }}>
                      <CheckIcon style={{ fontSize: 14 }} />
                    </IconButton>
                  ) : (
                    <IconButton size="small" onClick={e => { e.stopPropagation(); setEditingId(fleet.id); setEditName(fleet.name); }} style={{ color: '#94a3b8' }}>
                      <EditIcon style={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                  <IconButton size="small" onClick={e => { e.stopPropagation(); handleDelete(fleet.id); }} style={{ color: '#ef4444' }}>
                    <DeleteIcon style={{ fontSize: 14 }} />
                  </IconButton>
                  {isExpanded ? <ExpandLessIcon style={{ fontSize: 16, color: '#94a3b8' }} /> : <ExpandMoreIcon style={{ fontSize: 16, color: '#94a3b8' }} />}
                </Box>

                {/* Expanded: vehicles */}
                {isExpanded && (
                  <Box style={{ padding: '0 14px 12px', borderTop: '1px solid #f1f5f9' }}>
                    {fleetVehicles.length > 0 ? (
                      <Box style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                        {fleetVehicles.map(v => (
                          <Chip key={v.id} label={`${v.plate} ${v.type ? `(${v.type})` : ''}`}
                            size="small" onDelete={() => handleUnassignVehicle(fleet.id, v.id)}
                            style={{ fontSize: '0.68rem', fontWeight: 600, background: `${fleet.color}15`, color: fleet.color, border: `1px solid ${fleet.color}30` }} />
                        ))}
                      </Box>
                    ) : (
                      <Typography style={{ fontSize: '0.72rem', color: '#94a3b8', fontStyle: 'italic', padding: '8px 0' }}>
                        {pt ? 'Sem veículos atribuídos' : 'No vehicles assigned'}
                      </Typography>
                    )}
                    {/* Assign vehicle dropdown */}
                    {unassignedVehicles.length > 0 && (
                      <Box style={{ marginTop: 8 }}>
                        <Select
                          displayEmpty
                          value=""
                          onChange={e => { if (e.target.value) handleAssignVehicle(fleet.id, e.target.value); }}
                          variant="outlined"
                          size="small"
                          fullWidth
                          style={{ fontSize: '0.72rem', height: 32 }}
                        >
                          <MenuItem value="" disabled><em>{pt ? 'Adicionar veículo...' : 'Add vehicle...'}</em></MenuItem>
                          {unassignedVehicles.map(v => (
                            <MenuItem key={v.id} value={v.id} style={{ fontSize: '0.72rem' }}>
                              {v.plate} {v.type ? `(${v.type})` : ''}
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}

          {fleets.length === 0 && !loading && (
            <Box style={{ textAlign: 'center', padding: '32px 16px', color: '#94a3b8' }}>
              <LocalShippingIcon style={{ fontSize: 40, color: '#cbd5e1', marginBottom: 8 }} />
              <Typography style={{ fontSize: '0.82rem' }}>
                {pt ? 'Sem frotas criadas' : 'No fleets created'}
              </Typography>
              <Typography style={{ fontSize: '0.68rem', color: '#cbd5e1' }}>
                {pt ? 'Crie uma frota para agrupar os seus veículos' : 'Create a fleet to group your vehicles'}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
    <ConfirmDialog
      open={!!deleteConfirm}
      onClose={() => setDeleteConfirm(null)}
      onConfirm={confirmDelete}
      title={pt ? 'Eliminar Frota' : 'Delete Fleet'}
      message={pt ? 'Tem a certeza que deseja eliminar esta frota? Esta acção não pode ser revertida.' : 'Are you sure you want to delete this fleet? This action cannot be undone.'}
      severity="error"
      confirmLabel={pt ? 'Eliminar' : 'Delete'}
    />
    </>
  );
}

export default ManageFleetsDialog;
